"use client";

import { googleTagManager } from "@/components/common/utils";
import { getFormOneDataFromLocal } from "@/lib/edcrypt";
import React, { useEffect, useState } from "react";

interface NewStickyFooterProps {
  pageName?: string;
  variant?: "default" | "new";
}

const LOCAL_STORE_NAME = "MILES_NEXT_F1_REF_ENQUIRY";

const FORM_HIGHLIGHT_CLASS = "animate-sticky-pulse";

const pulseForm = (element: HTMLElement) => {
  const existingTimeout = element.dataset.highlightTimeout
    ? Number(element.dataset.highlightTimeout)
    : null;

  if (existingTimeout) {
    window.clearTimeout(existingTimeout);
  }

  void element.offsetWidth;

  element.classList.remove(FORM_HIGHLIGHT_CLASS);
  void element.offsetWidth;
  element.classList.add(FORM_HIGHLIGHT_CLASS);

  const timeoutId = window.setTimeout(() => {
    element.classList.remove(FORM_HIGHLIGHT_CLASS);
    delete element.dataset.highlightTimeout;
  }, 2200);

  element.dataset.highlightTimeout = String(timeoutId);
};

const getHeaderOffset = () => {
  const desktopHeader = document.querySelector(
    ".fixedHeader",
  ) as HTMLElement | null;
  const mobileHeader = document.querySelector(
    ".mobileHeader",
  ) as HTMLElement | null;

  const desktopOffset =
    desktopHeader && window.getComputedStyle(desktopHeader).display !== "none"
      ? desktopHeader.offsetHeight + 20
      : 0;

  const mobileOffset =
    mobileHeader && window.getComputedStyle(mobileHeader).display !== "none"
      ? mobileHeader.offsetHeight + 32
      : 0;

  return Math.max(24, desktopOffset, mobileOffset);
};

const focusFirstField = (element: HTMLElement) => {
  const firstField = element.querySelector<
    HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
  >(
    'input:not([type="hidden"]):not([disabled]), select:not([disabled]), textarea:not([disabled])',
  );

  firstField?.focus({ preventScroll: true });
};

const NewStickyFooter: React.FC<NewStickyFooterProps> = ({
  pageName = "CPA",
}) => {
  const [isFooterFixed, setIsFooterFixed] = useState<boolean>(true);
  const [isFormFilled, setIsFormFilled] = useState<boolean>(false);
  const isCombined = pageName === "CPA/CMA";

  useEffect(() => {
    const { status } = getFormOneDataFromLocal(LOCAL_STORE_NAME);
    setIsFormFilled(status);
  }, []);

  useEffect(() => {
    let ticking = false;
    let debounceTimer: ReturnType<typeof setTimeout> | null = null;

    const handleScroll = () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          const scrollPosition = window.scrollY;
          const windowHeight = window.innerHeight;
          const documentHeight = document.documentElement.scrollHeight;
          const distanceFromBottom =
            documentHeight - (scrollPosition + windowHeight);
          const shouldBeFixed = distanceFromBottom > 20;

          if (debounceTimer) {
            clearTimeout(debounceTimer);
          }
          debounceTimer = setTimeout(() => {
            setIsFooterFixed(shouldBeFixed);
          }, 50);

          ticking = false;
        });
        ticking = true;
      }
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    handleScroll();
    return () => {
      window.removeEventListener("scroll", handleScroll);
      if (debounceTimer) {
        clearTimeout(debounceTimer);
      }
    };
  }, []);

  const handleEnquireNow = () => {
    googleTagManager("CPA_LP_F_EnquireNow", {
      button_location: "sticky_footer",
      page_url: typeof window !== "undefined" ? window.location.href : "",
    });

    const formTarget = document.getElementById(
      "live-webinar-form",
    ) as HTMLElement | null;
    const scrollTarget =
      formTarget ??
      (document.getElementById("live-webinar-section") as HTMLElement | null);

    if (!scrollTarget) {
      return;
    }

    const targetTop =
      scrollTarget.getBoundingClientRect().top +
      window.scrollY -
      getHeaderOffset();

    window.scrollTo({ top: Math.max(targetTop, 0), behavior: "smooth" });

    if (formTarget) {
      const highlightDelay = window.innerWidth >= 900 ? 900 : 500;

      window.setTimeout(() => {
        pulseForm(formTarget);
        focusFirstField(formTarget);
      }, highlightDelay);
    }
  };

  if (isFormFilled) {
    return null;
  }

  return (
    <>
      {isFooterFixed && (
        <div
          className="h-[93px] pointer-events-none lg:h-[100px] pb-10"
          aria-hidden="true"
        />
      )}
      <div
        className={
          isFooterFixed
            ? "fixed bottom-0 left-1/2 right-auto -translate-x-1/2 w-[min(96%,560px)] z-[300] pointer-events-none lg:left-0 lg:right-0 lg:translate-x-0 lg:w-auto lg:px-4 lg:pb-[10px]"
            : "relative w-full bg-white border-t border-[#dee7f5] pt-6 md:pt-4 md:pb-2"
        }
        style={
          isFooterFixed
            ? { paddingBottom: "env(safe-area-inset-bottom, 0px)" }
            : undefined
        }
        role="complementary"
        aria-label="Webinar registration"
      >
        <div
          className={
            isFooterFixed
              ? "w-full h-[93px] mx-auto px-4 py-[14px] grid items-center gap-[14px] border border-[rgba(42,133,255,0.18)] rounded-[20px] shadow-[0_18px_36px_rgba(6,26,47,0.18)] backdrop-blur-[16px] pointer-events-auto bg-[rgba(255,255,255,0.7)] lg:max-w-[1200px] lg:px-[clamp(16px,2.4vw,24px)] lg:py-4 lg:gap-5"
              : "main_container px-4 py-[14px] grid items-center gap-[14px] lg:py-4 lg:gap-5"
          }
          style={{
            gridTemplateColumns: "minmax(0, 1fr) auto",
            WebkitBackdropFilter: isFooterFixed ? "blur(16px)" : undefined,
          }}
        >
          <div className="flex flex-col min-w-0">
            <div className="text-base font-bold text-[#1d1d1d] leading-[1.3] lg:text-lg">
              <span className="text-[#1d1d1d]">Be an </span>
              <span className="text-[#39f]">
                AI-Ready {isCombined ? "" : pageName}
              </span>
              <span className="text-[#1d1d1d]"> in 2026</span>
            </div>
            <div className="text-xs text-[#1d1d1d] mt-1 leading-[1.35]">
              Few seats left, <strong>hurry up</strong>
            </div>
          </div>

          <button
            type="button"
            onClick={handleEnquireNow}
            className="flex-shrink-0 min-w-[118px] bg-[#39f] text-white border-none rounded-lg text-sm font-bold px-[18px] py-3 cursor-pointer whitespace-nowrap shadow-[0_2px_12px_rgba(42,133,255,0.3)] transition-all duration-[180ms] hover:-translate-y-px hover:shadow-[0_5px_18px_rgba(42,133,255,0.34)] hover:bg-[#1a75ef] lg:min-w-[152px] lg:px-6"
            aria-label="Enquire now"
          >
            Enquire Now
          </button>
        </div>
      </div>
    </>
  );
};

export default NewStickyFooter;
